import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { Table, Tooltip } from "antd";
import {
  Upload,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Files,
  Eye,
  RotateCw,
  MoreVertical,
  RefreshCw,
  Info,
} from "lucide-react";
import { getAccessToken } from "../../services/authApi";
import {
  fetchUploadedDocuments,
  uploadDocument,
  getDashboardSummary,
} from "../../services/brokerDocUploadApi";
import "./BrokerDocUpload.css";

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const formatFileSize = (bytes) => {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }) + "  " + date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const StatusBadge = ({ status }) => {
  const config = {
    Processing: { className: "bdu-status-processing", icon: <RotateCw size={12} className="bdu-status-icon-spin" /> },
    Classified: { className: "bdu-status-classified", icon: <CheckCircle2 size={12} /> },
    Failed: { className: "bdu-status-failed", icon: <AlertTriangle size={12} /> },
  };
  const { className, icon } = config[status] || config.Processing;
  return (
    <span className={`bdu-status-badge ${className}`}>
      {icon} {status}
    </span>
  );
};

const DashboardCard = ({ title, count, description, icon, colorClass }) => (
  <div className={`bdu-dashboard-card ${colorClass}`}>
    <div className="bdu-dashboard-card-icon">{icon}</div>
    <div className="bdu-dashboard-card-content">
      <span className="bdu-dashboard-card-title">{title}</span>
      <span className="bdu-dashboard-card-count">{count}</span>
      <span className="bdu-dashboard-card-desc">{description}</span>
    </div>
  </div>
);

const BrokerDocUpload = () => {
  const [loanId, setLoanId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [summary, setSummary] = useState({ processing: 0, classified: 0, failed: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);

  // Initialize loan id from Encompass context
  useEffect(() => {
    if (window.elli && window.elli.script && window.elli.script.guest) {
      try {
        window.elli.script.guest.create();
        window.elli.script
          .getObject("loan")
          .then((loanObject) =>
            loanObject.all().then((loan) => {
              const id = loan?.id;
              if (id) setLoanId(id);
            })
          );
      } catch (err) {
        console.error("Error initializing Encompass context:", err);
      }
    } else {
      console.warn("elli.script.guest is not available.");
    }
  }, []);

  const getAuthCode = async () => {
    const authObj = await window.elli.script.getObject("auth");
    return authObj.createAuthCode();
  };

  const loadData = useCallback(async () => {
    if (!loanId) return;
    setLoading(true);
    try {
      const docs = await fetchUploadedDocuments(loanId);
      setDocuments(docs);
      setSummary(getDashboardSummary(docs));
    } catch (error) {
      console.error("Failed to load documents:", error);
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    if (loanId) {
      loadData();
    }
  }, [loanId, loadData]);

  const handleFilesUpload = useCallback(
    async (acceptedFiles) => {
      const newQueue = acceptedFiles.map((file) => ({
        id: `upload-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        file,
        progress: 0,
        status: "uploading",
      }));

      setUploadQueue((prev) => [...prev, ...newQueue]);

      let token;
      try {
        const authCode = await getAuthCode();
        token = await getAccessToken(authCode);
        if (!token) throw new Error("Unable to acquire access token");
      } catch {
        console.error("Failed to get access token");
        setUploadQueue((prev) =>
          prev.map((q) =>
            newQueue.find((n) => n.id === q.id)
              ? { ...q, status: "error", progress: 0 }
              : q
          )
        );
        return;
      }

      for (const item of newQueue) {
        try {
          const doc = await uploadDocument(
            item.file,
            token,
            loanId,
            "",
            (progress) => {
              setUploadQueue((prev) =>
                prev.map((q) => (q.id === item.id ? { ...q, progress } : q))
              );
            }
          );

          setUploadQueue((prev) =>
            prev.map((q) => (q.id === item.id ? { ...q, status: "done", progress: 100 } : q))
          );

          // Remove completed item from queue after a brief display
          setTimeout(() => {
            setUploadQueue((prev) => prev.filter((q) => q.id !== item.id));
          }, 1500);
        } catch {
          setUploadQueue((prev) =>
            prev.map((q) => (q.id === item.id ? { ...q, status: "error", progress: 0 } : q))
          );
        }
      }
    },
    [loanId]
  );

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const messages = rejectedFiles.map((r) => {
          const errors = r.errors.map((e) => e.message).join(", ");
          return `${r.file.name}: ${errors}`;
        });
        alert(`Some files were rejected:\n${messages.join("\n")}`);
      }
      if (acceptedFiles.length > 0) {
        handleFilesUpload(acceptedFiles);
      }
    },
    [handleFilesUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
  });

  const activeUploads = useMemo(
    () => uploadQueue.filter((q) => q.status === "uploading"),
    [uploadQueue]
  );

  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      render: (text) => (
        <span className="bdu-file-name-cell">
          <FileText size={16} className="bdu-file-icon" /> {text}
        </span>
      ),
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 100,
      render: (val) => formatFileSize(val),
    },
    {
      title: "Uploaded On",
      dataIndex: "uploadedOn",
      key: "uploadedOn",
      width: 200,
      render: (val) => formatDate(val),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => <StatusBadge status={status} />,
    },
    {
      title: "Document Type (AI)",
      dataIndex: "documentType",
      key: "documentType",
      width: 170,
      render: (val) => val || "—",
    },
    {
      title: "Folder",
      dataIndex: "folder",
      key: "folder",
      width: 130,
      render: (val) => val || "—",
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center",
      render: (_, record) => (
        <span className="bdu-actions-cell">
          {record.status === "Failed" ? (
            <Tooltip title="Retry">
              <button className="bdu-action-btn" onClick={() => {}}>
                <RotateCw size={16} />
              </button>
            </Tooltip>
          ) : (
            <Tooltip title="View">
              <button className="bdu-action-btn" onClick={() => {}}>
                <Eye size={16} />
              </button>
            </Tooltip>
          )}
          <Tooltip title="More">
            <button className="bdu-action-btn" onClick={() => {}}>
              <MoreVertical size={16} />
            </button>
          </Tooltip>
        </span>
      ),
    },
  ];

  return (
    <div className="bdu-container">
      {/* Header */}
      <div className="bdu-header">
        <h1 className="bdu-title">Upload Documents</h1>
        <p className="bdu-subtitle">
          Upload loan documents. Our system will classify and organize them automatically.
        </p>
      </div>

      {/* Upload Section */}
      <div className="bdu-upload-section">
        <div className="bdu-dropzone-wrapper">
          <div
            {...getRootProps()}
            className={`bdu-dropzone ${isDragActive ? "bdu-dropzone-active" : ""}`}
          >
            <input {...getInputProps()} />
            <div className="bdu-dropzone-content">
              <div className="bdu-dropzone-icon">
                <Upload size={28} />
              </div>
              <p className="bdu-dropzone-text">Drag & drop files here</p>
              <p className="bdu-dropzone-or">or</p>
              <button type="button" className="bdu-browse-btn">
                Browse Files
              </button>
              <p className="bdu-dropzone-hint">
                Supported formats: PDF, JPG, PNG &bull; Max file size: 5MB per file
              </p>
            </div>
          </div>

          {/* Upload Progress inside dropzone area */}
          {uploadQueue.length > 0 && (
            <div className="bdu-upload-progress-list">
              {uploadQueue.map((item) => (
                <div key={item.id} className="bdu-upload-progress-item">
                  <div className="bdu-upload-progress-info">
                    <FileText size={14} />
                    <span className="bdu-upload-progress-name">{item.file.name}</span>
                    <span className="bdu-upload-progress-pct">
                      {item.status === "error" ? "Failed" : `${item.progress}%`}
                    </span>
                  </div>
                  <div className="bdu-progress-bar-track">
                    <div
                      className={`bdu-progress-bar-fill ${item.status === "error" ? "bdu-progress-error" : ""} ${item.status === "done" ? "bdu-progress-done" : ""}`}
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upload Tips */}
        <div className="bdu-tips-card">
          <h3 className="bdu-tips-title">UPLOAD TIPS</h3>
          <ul className="bdu-tips-list">
            <li><CheckCircle2 size={14} className="bdu-tip-icon" /> You can upload multiple files at once</li>
            <li><CheckCircle2 size={14} className="bdu-tip-icon" /> Supported formats: PDF, JPG, PNG</li>
            <li><CheckCircle2 size={14} className="bdu-tip-icon" /> Maximum file size: 5MB per file</li>
            <li><CheckCircle2 size={14} className="bdu-tip-icon" /> Documents will be classified by AI</li>
            <li><CheckCircle2 size={14} className="bdu-tip-icon" /> We'll place them in the correct eFolder</li>
            <li><CheckCircle2 size={14} className="bdu-tip-icon" /> You can review and edit after upload</li>
          </ul>
        </div>
      </div>

      {/* Dashboard Summary */}
      <div className="bdu-dashboard">
        <DashboardCard
          title="PROCESSING"
          count={summary.processing}
          description="Files are being processed"
          icon={<RotateCw size={24} />}
          colorClass="bdu-card-processing"
        />
        <DashboardCard
          title="CLASSIFIED"
          count={summary.classified}
          description="Files classified successfully"
          icon={<CheckCircle2 size={24} />}
          colorClass="bdu-card-classified"
        />
        <DashboardCard
          title="FAILED"
          count={summary.failed}
          description="Files failed to process"
          icon={<AlertTriangle size={24} />}
          colorClass="bdu-card-failed"
        />
        <DashboardCard
          title="TOTAL UPLOADED"
          count={summary.total}
          description="Total files uploaded"
          icon={<Files size={24} />}
          colorClass="bdu-card-total"
        />
      </div>

      {/* Documents Grid */}
      <div className="bdu-grid-section">
        <div className="bdu-grid-header">
          <h2 className="bdu-grid-title">Uploaded Documents</h2>
          <button className="bdu-refresh-btn" onClick={loadData} disabled={loading}>
            <RefreshCw size={14} className={loading ? "bdu-status-icon-spin" : ""} />
            Refresh Status
          </button>
        </div>
        <Table
          columns={columns}
          dataSource={documents}
          rowKey="id"
          loading={loading}
          pagination={false}
          className="bdu-table"
          footer={() => (
            <div className="bdu-table-footer">
              Showing {documents.length} of {documents.length} files
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default BrokerDocUpload;
